import { useRef, useState } from 'react';
import { ApiError } from '../api/client';
import {
  useAdminUserscriptVersions,
  useDeleteUserscriptVersion,
  usePublishUserscriptVersion,
  useUploadUserscriptVersion,
} from '../api/queries';
import type { UserscriptVersion } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('de-DE') : '—';
}

/**
 * Userskript-Verwaltung: Administratoren laden neue Versionen hoch
 * (zunächst als Entwurf) und veröffentlichen genau eine davon -- die
 * veröffentlichte Version wird über `/userscript/twtool.user.js`
 * ausgeliefert (Download für Mitglieder UND Tampermonkeys automatisches
 * Update), siehe Dashboard-Box (`DashboardPage.tsx`).
 */
export function AdminUserscriptPage() {
  const versions = useAdminUserscriptVersions();
  const publish = usePublishUserscriptVersion();
  const remove = useDeleteUserscriptVersion();
  const toast = useToast();

  const rows = versions.data?.versions ?? [];

  const confirmPublish = (version: UserscriptVersion) => {
    publish.mutate(
      { id: version.id },
      {
        onSuccess: () => toast.push('success', `Version ${version.version} ist jetzt veröffentlicht.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const confirmDelete = (version: UserscriptVersion) => {
    if (!window.confirm(`Version ${version.version} wirklich löschen?`)) return;
    remove.mutate(
      { id: version.id },
      {
        onSuccess: () => toast.push('success', `Version ${version.version} wurde gelöscht.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Userskript-Verwaltung</h1>
          <p className="muted">
            Neue Versionen des Tampermonkey-Userskripts hochladen und veröffentlichen -- die
            veröffentlichte Version steht Mitgliedern zum Download bereit und wird von bereits
            installierten Kopien automatisch nachgeladen.
          </p>
        </div>
      </div>

      <UploadUserscriptForm />

      {versions.isPending && <Spinner label="Versionen werden geladen …" />}

      {versions.isError && (
        <div className="flash flash-error">
          {versions.error instanceof ApiError ? versions.error.message : 'Versionen konnten nicht geladen werden.'}
        </div>
      )}

      {!versions.isPending && rows.length === 0 && (
        <p className="muted center pad">Noch keine Version hochgeladen.</p>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Beschreibung</th>
                <th>Hochgeladen von</th>
                <th>Am</th>
                <th>Status</th>
                <th className="right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((version) => (
                <tr key={version.id}>
                  <td className="nowrap">{version.version}</td>
                  <td>{version.description ?? <span className="muted">—</span>}</td>
                  <td className="muted">{version.uploadedByName ?? '—'}</td>
                  <td className="nowrap">{formatDateTime(version.createdAt)}</td>
                  <td>
                    {version.isPublished ? (
                      <span className="badge badge-active">Veröffentlicht</span>
                    ) : (
                      <span className="badge badge-pending">Entwurf</span>
                    )}
                  </td>
                  <td className="right">
                    {!version.isPublished && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={publish.isPending}
                        onClick={() => confirmPublish(version)}
                      >
                        Veröffentlichen
                      </button>
                    )}{' '}
                    {!version.isPublished && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={remove.isPending}
                        onClick={() => confirmDelete(version)}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function UploadUserscriptForm() {
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadUserscriptVersion();
  const toast = useToast();

  const pickFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setFileName(file.name);
    setContent(await file.text());
  };

  const submit = () => {
    if (!content) return;
    upload.mutate(
      { content, description: description.trim() || undefined },
      {
        onSuccess: ({ version }) => {
          toast.push('success', `Version ${version.version} wurde als Entwurf hochgeladen.`);
          setFileName('');
          setContent('');
          setDescription('');
          if (inputRef.current) inputRef.current.value = '';
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="form" style={{ marginBottom: '16px' }}>
      <div className="field-row">
        <label className="field grow">
          <span>Userskript-Datei (.user.js)</span>
          <input
            ref={inputRef}
            type="file"
            accept=".js"
            onChange={(event) => void pickFile(event.target.files)}
          />
        </label>
        <label className="field grow">
          <span>Beschreibung (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="z. B. Änderungen in Kurzform"
          />
        </label>
        <button type="button" className="btn btn-primary" disabled={!content || upload.isPending} onClick={submit}>
          {upload.isPending ? 'Wird hochgeladen …' : 'Hochladen'}
        </button>
      </div>
      {fileName && <small className="hint">Ausgewählt: {fileName}</small>}
      <small className="hint">
        Version wird automatisch aus der "@version"-Zeile der Datei gelesen. Legt zunächst nur einen
        Entwurf an -- erst "Veröffentlichen" macht sie aktiv.
      </small>
    </div>
  );
}
